import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FiMail,
  FiShield,
  FiSettings,
  FiSave,
  FiSend,
  FiToggleLeft,
  FiToggleRight,
  FiLoader,
  FiAlertCircle,
  FiRefreshCw,
  FiTrash2,
} from 'react-icons/fi';
import Toast from './Toast';
import { API_URL } from '../config';

// Must match server masking; omit on PUT so the stored SMTP key is not overwritten by the placeholder
const SMTP_PASS_MASK = '••••••••';

function settingsPayloadForPut(settings) {
  const payload = JSON.parse(JSON.stringify(settings));
  if (payload.email?.smtpPass === SMTP_PASS_MASK) {
    delete payload.email.smtpPass;
  }
  return payload;
}

function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('email');
  const [smtpLogEvents, setSmtpLogEvents] = useState([]);
  const [smtpLogLoading, setSmtpLogLoading] = useState(false);
  const [smtpLogClearing, setSmtpLogClearing] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'email') {
      loadSmtpLog();
    }
  }, [activeTab]);

  const loadSmtpLog = async () => {
    setSmtpLogLoading(true);
    try {
      const response = await axios.get(`${API_URL}/settings/smtp-log`);
      setSmtpLogEvents(response.data.events || []);
    } catch (error) {
      console.error('Error loading SMTP log:', error);
    }
    setSmtpLogLoading(false);
  };

  const handleClearSmtpLog = async () => {
    if (!window.confirm('Clear all SMTP log entries on this server?')) return;
    setSmtpLogClearing(true);
    try {
      await axios.delete(`${API_URL}/settings/smtp-log`);
      setSmtpLogEvents([]);
      setToast({ message: 'SMTP log cleared', type: 'success' });
    } catch (error) {
      console.error('Error clearing SMTP log:', error);
      setToast({ message: error.response?.data?.error || 'Could not clear SMTP log', type: 'error' });
    }
    setSmtpLogClearing(false);
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`);
      setSettings(response.data.settings);
    } catch (error) {
      console.error('Error loading settings:', error);
      setToast({ message: 'Error loading settings', type: 'error' });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/settings`, settingsPayloadForPut(settings));
      setSettings(response.data.settings);
      setToast({ message: response.data.message, type: 'success' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setToast({ message: error.response?.data?.error || 'Error saving settings', type: 'error' });
    }
    setSaving(false);
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      // Save first, then test
      await axios.put(`${API_URL}/settings`, settingsPayloadForPut(settings));
      const response = await axios.post(`${API_URL}/settings/test-email`);
      setToast({ message: response.data.message, type: 'success' });
    } catch (error) {
      console.error('Error testing email:', error);
      setToast({ message: error.response?.data?.error || 'Error sending test email', type: 'error' });
    }
    setTestingEmail(false);
    loadSmtpLog();
  };

  const updateEmail = (field, value) => {
    setSettings(prev => ({
      ...prev,
      email: { ...prev.email, [field]: value }
    }));
  };

  const updateGeneral = (field, value) => {
    setSettings(prev => ({
      ...prev,
      general: { ...prev.general, [field]: value }
    }));
  };

  const updateSecurity = (field, value) => {
    setSettings(prev => ({
      ...prev,
      security: { ...prev.security, [field]: value }
    }));
  };

  const tabs = [
    { id: 'email', label: 'Email Service', icon: FiMail },
    { id: 'general', label: 'General', icon: FiSettings },
    { id: 'security', label: 'Security', icon: FiShield },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <FiLoader className="animate-spin text-3xl text-[#3c79bb]" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 py-6">
        <div className="glass-card rounded-2xl p-6 text-center">
          <FiAlertCircle className="text-4xl text-red-500 mx-auto mb-3" />
          <p className="text-gray-900 font-semibold">Failed to load settings.</p>
          <button onClick={loadSettings} className="mt-3 text-[#3c79bb] hover:underline font-semibold text-sm">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-lg">Settings</h1>
        <p className="text-white/80 text-sm sm:text-base font-medium mt-1">
          Configure your PW Connect application
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 sm:gap-2 mb-4 sm:mb-6 overflow-x-auto pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-semibold whitespace-nowrap transition-all duration-200 ${
              activeTab === tab.id
                ? 'bg-[#3c79bb] text-white shadow-lg shadow-[#3c79bb]/30'
                : 'bg-white/70 backdrop-blur-md border border-white/40 text-gray-900 hover:bg-white/85 shadow-sm'
            }`}
          >
            <tab.icon className="text-base sm:text-lg" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="glass-card rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden">

        {/* ──── Email Service Tab ──── */}
        {activeTab === 'email' && (
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Email Service</h2>
                <p className="text-xs sm:text-sm text-gray-600 font-medium mt-1">
                  Configure SMTP for password reset emails. Default host is Brevo (
                  <code className="text-[11px] bg-gray-100/80 px-1 rounded">smtp-relay.brevo.com</code>
                  , port 587). Use your Brevo SMTP login and SMTP key from the Brevo dashboard.
                </p>
              </div>
              <button
                onClick={() => updateEmail('enabled', !settings.email.enabled)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm ${
                  settings.email.enabled
                    ? 'bg-green-100/90 text-green-800 border border-green-400'
                    : 'bg-white/70 text-gray-700 border border-gray-300'
                }`}
              >
                {settings.email.enabled ? (
                  <>
                    <FiToggleRight className="text-xl text-green-600" />
                    <span className="hidden sm:inline">Enabled</span>
                  </>
                ) : (
                  <>
                    <FiToggleLeft className="text-xl" />
                    <span className="hidden sm:inline">Disabled</span>
                  </>
                )}
              </button>
            </div>

            <div className={`space-y-4 sm:space-y-5 ${!settings.email.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">SMTP Host</label>
                  <input
                    type="text"
                    value={settings.email.smtpHost}
                    onChange={(e) => updateEmail('smtpHost', e.target.value)}
                    placeholder="smtp-relay.brevo.com"
                    className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-300/60 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent transition-all text-gray-900 placeholder-gray-500 font-medium text-sm shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">SMTP Port</label>
                  <input
                    type="number"
                    value={settings.email.smtpPort}
                    onChange={(e) => updateEmail('smtpPort', e.target.value)}
                    placeholder="587"
                    className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-300/60 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent transition-all text-gray-900 placeholder-gray-500 font-medium text-sm shadow-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    587 (recommended), 2525 if your host blocks 587, 465 for SSL
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">SMTP Username</label>
                  <input
                    type="text"
                    value={settings.email.smtpUser}
                    onChange={(e) => updateEmail('smtpUser', e.target.value)}
                    placeholder="Brevo SMTP login"
                    className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-300/60 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent transition-all text-gray-900 placeholder-gray-500 font-medium text-sm shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">SMTP Password</label>
                  <input
                    type="password"
                    value={settings.email.smtpPass}
                    onChange={(e) => updateEmail('smtpPass', e.target.value)}
                    placeholder="Brevo SMTP key (xsmtpsib-…)"
                    className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-300/60 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent transition-all text-gray-900 placeholder-gray-500 font-medium text-sm shadow-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    In Brevo: open SMTP and API, then SMTP, and create a key. Or set BREVO_SMTP_LOGIN and BREVO_SMTP_KEY in server .env.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">From Email Address</label>
                  <input
                    type="email"
                    value={settings.email.fromAddress}
                    onChange={(e) => updateEmail('fromAddress', e.target.value)}
                    placeholder="noreply@yourcompany.com"
                    className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-300/60 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent transition-all text-gray-900 placeholder-gray-500 font-medium text-sm shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1.5">From Display Name</label>
                  <input
                    type="text"
                    value={settings.email.fromName}
                    onChange={(e) => updateEmail('fromName', e.target.value)}
                    placeholder="PW Connect"
                    className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-300/60 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent transition-all text-gray-900 placeholder-gray-500 font-medium text-sm shadow-sm"
                  />
                </div>
              </div>

              <div className="pt-2">
                <button
                  onClick={handleTestEmail}
                  disabled={testingEmail || !settings.email.enabled}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white/70 backdrop-blur-sm text-[#3c79bb] border border-[#3c79bb]/40 rounded-xl hover:bg-white/90 transition-all duration-200 font-semibold text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testingEmail ? (
                    <FiLoader className="animate-spin text-base" />
                  ) : (
                    <FiSend className="text-base" />
                  )}
                  {testingEmail ? 'Sending...' : 'Send Test Email'}
                </button>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200/70">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-base font-bold text-gray-900">SMTP log</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Recent sends and failures (server memory only; restarting the app clears this).
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={loadSmtpLog}
                    disabled={smtpLogLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-gray-300/80 bg-white/70 text-gray-800 hover:bg-white/90 disabled:opacity-50"
                  >
                    <FiRefreshCw className={`text-sm ${smtpLogLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                  <button
                    type="button"
                    onClick={handleClearSmtpLog}
                    disabled={smtpLogClearing || smtpLogEvents.length === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-red-200 bg-red-50/80 text-red-800 hover:bg-red-50 disabled:opacity-50"
                  >
                    {smtpLogClearing ? <FiLoader className="animate-spin text-sm" /> : <FiTrash2 className="text-sm" />}
                    Clear
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200/80 bg-white/50 max-h-72 overflow-y-auto">
                {smtpLogLoading && smtpLogEvents.length === 0 ? (
                  <div className="flex items-center justify-center py-10 text-gray-500 text-sm">
                    <FiLoader className="animate-spin mr-2" />
                    Loading…
                  </div>
                ) : smtpLogEvents.length === 0 ? (
                  <p className="py-8 px-4 text-center text-sm text-gray-500">
                    No SMTP events yet. Send a test email or trigger a password reset to see entries here.
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {smtpLogEvents.map((ev) => (
                      <li key={ev.id} className="px-3 py-2.5 sm:px-4 text-left">
                        <div className="flex flex-wrap items-center gap-2 gap-y-1">
                          <span
                            className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                              ev.level === 'ok'
                                ? 'bg-green-100 text-green-800'
                                : ev.level === 'err'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {ev.level === 'ok' ? 'OK' : ev.level === 'err' ? 'Error' : 'Info'}
                          </span>
                          <time className="text-[11px] text-gray-500 tabular-nums">
                            {new Date(ev.at).toLocaleString()}
                          </time>
                        </div>
                        <p className="text-sm font-medium text-gray-900 mt-1">{ev.message}</p>
                        {ev.detail ? (
                          <pre className="mt-1.5 text-[11px] leading-snug text-gray-600 whitespace-pre-wrap break-words font-mono bg-gray-50/80 rounded-lg px-2 py-1.5 border border-gray-100">
                            {ev.detail}
                          </pre>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ──── General Tab ──── */}
        {activeTab === 'general' && (
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="mb-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">General Settings</h2>
              <p className="text-xs sm:text-sm text-gray-600 font-medium mt-1">
                App-wide configuration options
              </p>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">Company / App Name</label>
                <input
                  type="text"
                  value={settings.general.companyName}
                  onChange={(e) => updateGeneral('companyName', e.target.value)}
                  placeholder="PW Connect"
                  className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-300/60 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent transition-all text-gray-900 placeholder-gray-500 font-medium text-sm shadow-sm max-w-md"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Session Timeout (hours)
                </label>
                <input
                  type="number"
                  value={settings.general.sessionTimeoutHours}
                  onChange={(e) => updateGeneral('sessionTimeoutHours', e.target.value)}
                  min={1}
                  max={720}
                  className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-300/60 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent transition-all text-gray-900 placeholder-gray-500 font-medium text-sm shadow-sm max-w-xs"
                />
                <p className="text-xs text-gray-500 mt-1">
                  How long a user stays logged in. Default is 168 hours (7 days).
                </p>
              </div>

              <div className="flex items-center justify-between max-w-md bg-white/60 backdrop-blur-sm rounded-xl px-4 py-3 border border-gray-300/60 shadow-sm">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Allow Self Registration</p>
                  <p className="text-xs text-gray-600 mt-0.5">Let new users create their own accounts</p>
                </div>
                <button
                  onClick={() => updateGeneral('allowSelfRegistration', !settings.general.allowSelfRegistration)}
                  className="flex-shrink-0"
                >
                  {settings.general.allowSelfRegistration ? (
                    <FiToggleRight className="text-2xl text-green-600" />
                  ) : (
                    <FiToggleLeft className="text-2xl text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ──── Security Tab ──── */}
        {activeTab === 'security' && (
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="mb-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">Security Settings</h2>
              <p className="text-xs sm:text-sm text-gray-600 font-medium mt-1">
                Password policies and login protection
              </p>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Minimum Password Length
                </label>
                <input
                  type="number"
                  value={settings.security.minPasswordLength}
                  onChange={(e) => updateSecurity('minPasswordLength', e.target.value)}
                  min={6}
                  max={32}
                  className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-300/60 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent transition-all text-gray-900 placeholder-gray-500 font-medium text-sm shadow-sm max-w-xs"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters. Default is 8.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Max Login Attempts (per 15 min)
                </label>
                <input
                  type="number"
                  value={settings.security.maxLoginAttempts}
                  onChange={(e) => updateSecurity('maxLoginAttempts', e.target.value)}
                  min={3}
                  max={50}
                  className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-300/60 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-transparent transition-all text-gray-900 placeholder-gray-500 font-medium text-sm shadow-sm max-w-xs"
                />
                <p className="text-xs text-gray-500 mt-1">
                  After this many failed attempts, user is temporarily locked out. Default is 5.
                </p>
              </div>

              <div className="flex items-center justify-between max-w-md bg-white/60 backdrop-blur-sm rounded-xl px-4 py-3 border border-gray-300/60 shadow-sm">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Force Password Reset on First Login</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    New users must change their password after first login
                  </p>
                </div>
                <button
                  onClick={() => updateSecurity('requirePasswordReset', !settings.security.requirePasswordReset)}
                  className="flex-shrink-0"
                >
                  {settings.security.requirePasswordReset ? (
                    <FiToggleRight className="text-2xl text-green-600" />
                  ) : (
                    <FiToggleLeft className="text-2xl text-gray-400" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ──── Save Bar ──── */}
        <div className="border-t border-gray-200/50 px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between bg-white/40">
          <p className="text-xs sm:text-sm text-gray-600 font-medium">
            Changes are not applied until you save.
          </p>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-[#3c79bb] text-white px-5 sm:px-6 py-2.5 rounded-xl hover:bg-[#306196] focus:outline-none focus:ring-2 focus:ring-[#3c79bb] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 font-semibold text-sm sm:text-base"
          >
            {saving ? (
              <FiLoader className="animate-spin text-base" />
            ) : (
              <FiSave className="text-base" />
            )}
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default Settings;
